import React, { useState } from 'react';
import { FormControl, Grid, makeStyles, MenuItem, Select, Theme, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { LocalVideoTrack } from 'twilio-video';
import VideoTrack from '../VideoTrack/VideoTrack';
import useVideoContext from '../../hooks/useVideoContext/useVideoContext';
import { DEFAULT_VIDEO_CONSTRAINTS, SELECTED_VIDEO_INPUT_KEY } from '../../constants';
import useDevices from '../../hooks/useDevices/useDevices';
import useMediaStreamTrack from '../../hooks/useMediaStreamTrack/useMediaStreamTrack';

const useStyles = makeStyles((theme: Theme) => ({
    container: {
        position: 'relative',
        height: 0,
        overflow: 'hidden',
        paddingTop: `${(9 / 16) * 50}%`,
        width: '50%',
        background: 'black',
    },
    innerContainer: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
    },
    identityContainer: {
        position: 'absolute',
        bottom: 0,
        zIndex: 1,
    },
    identity: {
        background: 'rgba(0, 0, 0, 0.5)',
        color: 'white',
        padding: '0.18em 0.3em',
        margin: 0,
        display: 'flex',
        alignItems: 'center',
    },
    avatarContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'black',
        position: 'absolute',
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        zIndex: 1,
        [theme.breakpoints.down('sm')]: {
            '& svg': {
                transform: 'scale(0.7)',
            },
        },
    },
    videoSelectContainer: {
        margin: '15px 0px',
    },
}));

export default function LocalVideoPreview() {
    const classes = useStyles();
    const { t } = useTranslation();
    const { localTracks } = useVideoContext();
    const { videoInputDevices } = useDevices();
    const [storedLocalVideoDeviceId, setStoredLocalVideoDeviceId] = useState(
        window.localStorage.getItem(SELECTED_VIDEO_INPUT_KEY),
    );

    const videoTrack = localTracks.find(
        track => !track.name.includes('screen') && track.kind === 'video',
    ) as LocalVideoTrack;
    const mediaStreamTrack = useMediaStreamTrack(videoTrack);
    const localVideoInputDeviceId = mediaStreamTrack?.getSettings().deviceId || storedLocalVideoDeviceId;

    function replaceTrack(newDeviceId: string) {
        // Here we store the device ID in the component state. This is so we can re-render this component display
        // to display the name of the selected device when it is changed while the users camera is off.
        setStoredLocalVideoDeviceId(newDeviceId);
        window.localStorage.setItem(SELECTED_VIDEO_INPUT_KEY, newDeviceId);
        videoTrack?.restart({
            // eslint-disable-next-line @typescript-eslint/ban-types
            ...(DEFAULT_VIDEO_CONSTRAINTS as {}),
            deviceId: { exact: newDeviceId },
        });
    }

    return (
        <Grid container xs={12} direction="column">
            <Typography variant="h5">{t('Video Preview')}</Typography>
            <div className={classes.container}>
                <div className={classes.innerContainer}>
                    {videoTrack ? (
                        <VideoTrack track={videoTrack} isLocal />
                    ) : (
                        <div className={classes.avatarContainer}>Loading</div>
                    )}
                </div>
            </div>
            <Grid container item className={classes.videoSelectContainer}>
                {videoInputDevices.length > 0 ? (
                    <FormControl fullWidth>
                        <Grid
                            container
                            direction="row"
                            xs={6}
                            justify="flex-start"
                            alignContent="center"
                            alignItems="center"
                        >
                            <Select
                                onChange={e => replaceTrack(e.target.value as string)}
                                value={localVideoInputDeviceId || ''}
                                variant="outlined"
                                fullWidth
                            >
                                {videoInputDevices.map(device => (
                                    <MenuItem value={device.deviceId} key={device.deviceId}>
                                        {device.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </Grid>
                    </FormControl>
                ) : (
                    <Typography>{videoTrack?.mediaStreamTrack.label || 'No Local Video'}</Typography>
                )}
            </Grid>
        </Grid>
    );
}
